/**
 * API client for My Saved Views (#1144).
 *
 * Same endpoint family as the old Saved Filters — a view IS that row, extended
 * with columns / workspaceIds / isPrimary. Kept as one service so there is
 * exactly one way to touch users_bills_views from the app.
 */

import api from '../lib/api';
import type { SavedView, SavedViewColumns } from '../types/savedViews';

export interface SaveViewPayload {
  name: string;
  /** Owning organization (PRD §15) — required on create; the author must administer it. */
  organizationId?: string;
  filters: Record<string, unknown>;
  searchQuery?: string;
  columns?: SavedViewColumns | null;
  workspaceIds?: string[];
  isPrimary?: boolean;
}

export async function fetchSavedViews(): Promise<SavedView[]> {
  const response = await api.get('/saved-filters');
  if (!response.data.success) throw new Error('Failed to fetch saved views');
  return response.data.data as SavedView[];
}

export async function createSavedView(payload: SaveViewPayload): Promise<SavedView> {
  const response = await api.post('/saved-filters', payload);
  if (!response.data.success) throw new Error('Failed to create saved view');
  return response.data.data as SavedView;
}

export async function updateSavedView(id: string, payload: SaveViewPayload): Promise<SavedView> {
  const response = await api.put(`/saved-filters/${id}`, payload);
  if (!response.data.success) throw new Error('Failed to update saved view');
  return response.data.data as SavedView;
}

export async function setPrimarySavedView(id: string, isPrimary: boolean): Promise<SavedView> {
  const response = await api.patch(`/saved-filters/${id}/primary`, { isPrimary });
  if (!response.data.success) throw new Error('Failed to set primary view');
  return response.data.data as SavedView;
}

export async function deleteSavedView(id: string): Promise<void> {
  const response = await api.delete(`/saved-filters/${id}`);
  if (!response.data.success) throw new Error('Failed to delete saved view');
}
